import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RESERVATION_STATUSES, type ReservationRow, type ReservationStatus } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { STATUS_LABEL } from '../lib/format';

/**
 * The legacy edit form's "Nastavení → Stav" row: the booking's status as a
 * strip of buttons — Nová · Předplaceno · Aktivní (parkuje) · Uzavřena ·
 * Propadlá · Zrušeno · Smazáno — the current one lit, any other one press
 * away. The owner, first day of writes (20 October 2026): "we use all of
 * it" — undoing a wrong close, deleting a duplicate, marking an agency
 * booking Předplaceno.
 *
 * It is a correction tool, so it asks once: the press opens a line with a
 * reason (required for Zrušeno and Smazáno, optional otherwise) and "Uložit", as the old form saved with Uložit. The
 * server keeps the stamps honest (write/reservations.ts setStatus,
 * `correction`) and journals every column with the reason on the status
 * row. Same order as the old strip; the legacy left out Připravena
 * (`pending`), which no row carries, and so does this.
 */
const ORDER: ReservationStatus[] = ['new', 'paid', 'verified', 'closed', 'noshow', 'canceled', 'deleted'];

export function StavRow({ r }: { r: ReservationRow }) {
  const qc = useQueryClient();
  const [to, setTo] = useState<ReservationStatus | null>(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (next: ReservationStatus) => api.setStatus(r.id, { to: next, note: note.trim(), correction: true }),
    onSuccess: async (_rc, next) => {
      setDone(`✔ Stav změněn: ${STATUS_LABEL[next] ?? next}`);
      setTo(null);
      setNote('');
      await qc.invalidateQueries();
    },
  });

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  /* Zrušeno and Smazáno need a reason (the owner, 31 October 2026: dispatchers delete duplicates, "but always
     write why"); the server refuses them without one too. */
  const reasonRequired = to === 'canceled' || to === 'deleted';

  const statuses = ORDER.filter((s) => (RESERVATION_STATUSES as readonly string[]).includes(s));

  return (
    <div className="stavrow">
      <div className="mtabs" role="radiogroup" aria-label="Stav rezervace">
        <span className="stavrow-label">Stav:</span>
        {statuses.map((s) => (
          <button
            key={s}
            type="button"
            className={s === r.status ? 'active' : ''}
            aria-pressed={s === r.status}
            disabled={m.isPending || r.reslock}
            title={r.reslock ? 'Rezervace je zamčená' : s === r.status ? 'Aktuální stav' : `Změnit stav na ${STATUS_LABEL[s] ?? s}`}
            onClick={() => {
              if (s === r.status) return;
              setDone(null);
              setTo(s);
            }}
          >
            {STATUS_LABEL[s] ?? s}
          </button>
        ))}
      </div>
      {to && (
        <form
          className="arrive-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!m.isPending) m.mutate(to);
          }}
        >
          <span className="depart-owed">
            Změnit stav <strong>{STATUS_LABEL[r.status] ?? r.status}</strong> → <strong>{STATUS_LABEL[to] ?? to}</strong>
          </span>
          <label>
            Důvod
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={reasonRequired ? 'povinné — proč (např. duplicita k ID …)' : 'nepovinné, do historie změn'}
              required={reasonRequired}
              minLength={reasonRequired ? 3 : undefined}
              maxLength={255}
              autoFocus
            />
          </label>
          <button type="submit" className="btn primary" disabled={m.isPending || (reasonRequired && note.replace(/\u00a0/g, ' ').trim().length < 3)}>
            {m.isPending ? 'Ukládám…' : 'Uložit'}
          </button>
          <button type="button" className="btn" onClick={() => setTo(null)} disabled={m.isPending}>
            zrušit editaci
          </button>
          {message && <span className="arrive-error">{message}</span>}
        </form>
      )}
      {done && !to && <span className="arrived-mark">{done}</span>}
    </div>
  );
}
